import React, { useEffect, useRef } from 'react';

export interface FanItem {
  tag: string;
  image?: string;
}

export interface TagPickerWheelProps {
  fans: FanItem[];
  selectedIndex: number;
}

const ITEM_HEIGHT = 20;

export const TagPickerWheel: React.FC<TagPickerWheelProps> = ({
  fans,
  selectedIndex,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemsContainerRef = useRef<HTMLDivElement>(null);
  const lastIndexRef = useRef<number>(selectedIndex);
  const isInitialMountRef = useRef<boolean>(true);

  useEffect(() => {
    if (!itemsContainerRef.current) return;

    const currentTranslateY = -lastIndexRef.current * ITEM_HEIGHT;
    const targetTranslateY = -selectedIndex * ITEM_HEIGHT;

    // On init, set position without animation
    if (isInitialMountRef.current) {
      itemsContainerRef.current.style.transition = 'none';
      itemsContainerRef.current.style.transform = `translateY(${targetTranslateY}px)`;
      // Force a reflow
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      itemsContainerRef.current.offsetHeight;
      isInitialMountRef.current = false;
      lastIndexRef.current = selectedIndex;
      return;
    }

    // Only animate if the index actually changed
    if (lastIndexRef.current === selectedIndex) {
      return;
    }

    const distance = Math.abs(selectedIndex - lastIndexRef.current);
    const isLongTraversal = distance > fans.length / 2;

    const transitionDuration = isLongTraversal ? '300ms' : '400ms';
    const transitionTiming = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

    itemsContainerRef.current.style.transition = 'none';
    itemsContainerRef.current.style.transform = `translateY(${currentTranslateY}px)`;

    // force a reflow to ensure current position is applied
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    itemsContainerRef.current.offsetHeight;

    requestAnimationFrame(() => {
      if (itemsContainerRef.current) {
        itemsContainerRef.current.style.transition = `transform ${transitionDuration} ${transitionTiming}`;
        itemsContainerRef.current.style.transform = `translateY(${targetTranslateY}px)`;
      }
    });

    lastIndexRef.current = selectedIndex;
  }, [selectedIndex, fans.length]);

  return (
    <div
      ref={containerRef}
      className="overflow-hidden self-start"
      style={{
        height: ITEM_HEIGHT,
      }}>
      <div ref={itemsContainerRef} className="flex flex-col items-start">
        {fans.map((fan, index) => (
          <div
            key={`${fan.tag}-${index}`}
            className="flex flex-row items-center gap-0.5 bg-blue-100 rounded-full p-0 shrink-0"
            style={{
              height: ITEM_HEIGHT,
            }}>
            {fan.image && (
              <div className="w-5 h-5 rounded-full overflow-hidden shrink-0">
                <img
                  src={fan.image}
                  alt={fan.tag}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
            <span className="text-black text-sm font-semibold">@{fan.tag}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TagPickerWheel;
